<template>
    <side-panel class="side-panel" id="notifications-panel">
        <template #head>
            <div class="d-head">
                <div class="container-fluid">
                    <h3 class="panel-title">{{ __('notifications') }}</h3>

                    <div class="close-btn" @click="$emit('toggleUtilityPanel')">
                        <i class="fa-solid fa-xmark"></i>
                    </div>
                </div>
            </div>
        </template>

        <template #body>
            <div class="notifications-content">
                <notification-rule-form v-if="ruleFormOpen" :rule="selectedRule" :mode="ruleFormMode"
                    :event-categories="eventCategories" :company-tags="companyTags" :vehicles="storeTrucks"
                    :pois="storePois" :poi-categories="poiCategories" :saving="notificationsSaving" @save="saveRule"
                    @cancel="closeRuleForm" />
                <template v-else>
                    <div class="notifications-filters">
                        <div class="input-group search-filter">
                            <span class="input-group-text">
                                <i class="fa-regular fa-magnifying-glass"></i>
                            </span>
                            <input v-model.trim="filters.query" class="form-control notification-control search-input"
                                type="search" :placeholder="__('notification_management.search_placeholder')"
                                :aria-label="__('notification_management.search_placeholder')" />
                            <select v-model="filters.searchBy" class="form-select notification-control search-scope"
                                :aria-label="__('notification_management.search_scope')">
                                <option v-for="option in searchByOptions" :key="option.value" :value="option.value">
                                    {{ option.text }}
                                </option>
                            </select>
                        </div>

                        <select v-model="filters.event" class="form-select notification-control filter-select"
                            :aria-label="__('notification_management.event_filter')">
                            <option value="all">
                                {{ __('notification_management.all_events') }}
                            </option>
                            <optgroup v-for="category in eventCategories" :key="category.key" :label="category.label">
                                <option v-for="event in category.events" :key="event.value" :value="event.value"
                                    :disabled="event.disabled">
                                    {{ event.label }}
                                </option>
                            </optgroup>
                        </select>

                        <!-- <select v-model="filters.channel"
                            class="form-select notification-control filter-select channel-filter"
                            :aria-label="__('notification_management.channel_filter')">
                            <option v-for="option in channelOptions" :key="option.value" :value="option.value">
                                {{ option.text }}
                            </option>
                        </select> -->

                        <select v-model="filters.destination"
                            class="form-select notification-control filter-select destination-filter"
                            :aria-label="__('notification_management.destination_filter')">
                            <option value="all">
                                {{ __('notification_management.all_destinations') }}
                            </option>
                            <optgroup v-for="group in destinationGroups" :key="group.channel" :label="group.label">
                                <option v-for="destination in group.destinations" :key="destination.value"
                                    :value="destination.value">
                                    {{ destination.label }}
                                </option>
                            </optgroup>
                        </select>

                        <golia-button class="reset-filters-button" variant="btn-fill gray"
                            icon="fa-fw fa-arrow-rotate-left" :label="__('notification_management.reset_filters')"
                            :disabled="!hasActiveFilters" @click="resetFilters" />

                        <golia-button class="new-rule-button" variant="btn-fill dark-blue" icon="fa-fw fa-plus"
                            :label="__('notification_management.new_rule')" @click="openCreateForm" />
                    </div>

                    <div class="notification-views">
                        <div class="view-tabs" role="tablist" :aria-label="__('notification_management.views')">
                            <button type="button" class="view-tab" :class="{ active: activeView === 'events' }"
                                role="tab" :aria-selected="activeView === 'events'"
                                aria-controls="notification-rules-view" @click="activeView = 'events'">
                                {{ __('notification_management.event_list') }}
                            </button>
                            <button type="button" class="view-tab" :class="{ active: activeView === 'categories' }"
                                role="tab" :aria-selected="activeView === 'categories'"
                                aria-controls="notification-rules-view" @click="activeView = 'categories'">
                                {{ __('notification_management.category_event_list') }}
                            </button>
                        </div>

                        <div id="notification-rules-view" class="notification-table-wrapper" role="tabpanel">
                            <div v-if="notificationsLoading" class="notifications-state">
                                <i class="fa-solid fa-spinner fa-spin"></i>
                                {{ __('notification_management.loading_rules') }}
                            </div>
                            <div v-else-if="notificationsError" class="notifications-state error-state">
                                <i class="fa-solid fa-triangle-exclamation"></i>
                                <span>
                                    {{ __('notification_management.load_rules_error') }}
                                </span>
                                <golia-button variant="btn-fill dark-blue" icon="fa-fw fa-rotate-right"
                                    :label="__('retry')" @click="loadNotificationRules" />
                            </div>
                            <table v-else class="notification-table">
                                <thead>
                                    <tr>
                                        <th>{{ __('notification_management.name_event') }}</th>
                                        <th width="150">{{ __('notification_management.applied_to') }}</th>
                                        <th>{{ __('notification_management.main_parameters') }}</th>
                                        <th class="text-center">{{ __('notification_management.channels') }}</th>
                                        <th width="120" class="text-center">{{ __('notification_management.language') }}
                                        </th>
                                        <th class="text-center">
                                            {{ __('notification_management.actions') }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="row in tableRows" :key="row.key"
                                        :class="{ 'category-group-row': row.type === 'category' }">
                                        <template v-if="row.type === 'category'">
                                            <td colspan="6" class="category-group-cell">
                                                <span class="category-icon">
                                                    <i class="fa-solid" :class="row.category.icon"></i>
                                                </span>
                                                <strong>{{ row.category.label }}</strong>
                                                <span class="category-rule-counter">
                                                    {{ row.ruleCount }}
                                                </span>
                                            </td>
                                        </template>
                                        <template v-else>
                                            <td>
                                                <div class="rule-identity">
                                                    <strong>{{ row.rule.ruleName }}</strong>
                                                    <span v-if="activeView === 'categories'" class="event-name">
                                                        {{
                                                            eventLookup[row.rule.event.type]?.label ||
                                                            row.rule.event.type
                                                        }}
                                                    </span>
                                                    <span v-if="activeView !== 'categories'"
                                                        class="rule-chip event-category">
                                                        {{
                                                            eventLookup[row.rule.event.type]
                                                                ?.categoryLabel ||
                                                            row.rule.event.category ||
                                                            '-'
                                                        }}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <span v-if="row.rule.application.type === 'fleet'"
                                                    class="rule-chip application-chip">
                                                    {{ row.rule.application.label }}
                                                </span>
                                                <div v-else class="application-groups">
                                                    <div v-if="row.rule.application.tags.length"
                                                        class="application-group">
                                                        <strong>
                                                            Tags:
                                                        </strong>
                                                        <span>{{ row.rule.application.tags.join(', ') }};</span>
                                                    </div>
                                                    <div v-if="row.rule.application.plates.length"
                                                        class="application-group">
                                                        <strong>
                                                            {{ __('notification_management.vehicle_plates') }}:
                                                        </strong>
                                                        <span>{{ row.rule.application.plates.join(', ') }};</span>
                                                    </div>
                                                    <span v-if="!row.rule.application.tags.length &&
                                                        !row.rule.application.plates.length
                                                    ">
                                                        -
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <p class="parameters-description">
                                                    {{ row.rule.mainParameters }}
                                                </p>
                                            </td>
                                            <td class="text-center">
                                                <div class="rule-channels">
                                                    <div class="channel-list align-items-center justify-content-center">
                                                        <span class="channel-status" :class="{
                                                            enabled:
                                                                row.rule.channels.email.enabled
                                                        }" :title="channelTitle(
                                                            'email',
                                                            row.rule.channels.email
                                                        )
                                                            " :aria-label="channelTitle(
                                                                'email',
                                                                row.rule.channels.email
                                                            )
                                                                ">
                                                            <i class="fa-solid fa-envelope"></i>
                                                            <span class="channel-counter">
                                                                {{ row.rule.channels.email.count }}
                                                            </span>
                                                        </span>
                                                        <!-- <span class="channel-status" :class="{
                                                            enabled: row.rule.channels.sms.enabled
                                                        }" :title="channelTitle(
                                                            'sms',
                                                            row.rule.channels.sms
                                                        )
                                                            " :aria-label="channelTitle(
                                                                'sms',
                                                                row.rule.channels.sms
                                                            )
                                                                ">
                                                            <i class="fa-solid fa-message-sms"></i>
                                                            <span class="channel-counter">
                                                                {{ row.rule.channels.sms.count }}
                                                            </span>
                                                        </span> -->
                                                    </div>
                                                    <small class="recipients-summary">
                                                        {{ recipientsSummary(row.rule) }}
                                                    </small>
                                                </div>
                                            </td>
                                            <td class="text-center">
                                                <span>
                                                    {{ languageLabel(row.rule.language) }}
                                                </span>
                                            </td>
                                            <td>
                                                <div class="rule-actions">
                                                    <golia-button variant="btn-info btn-sm" icon="fa-fw fa-pencil"
                                                        :title="__('edit')" :aria-label="__('edit')"
                                                        @click="openEditForm(row.rule)" />
                                                    <golia-button variant="btn-info btn-sm" icon="fa-fw fa-copy"
                                                        :title="__('notification_management.clone_rule')"
                                                        :aria-label="__('notification_management.clone_rule')"
                                                        @click="openCloneForm(row.rule)" />
                                                    <golia-button variant="btn-danger btn-sm" icon="fa-fw fa-trash"
                                                        :title="__('delete')" :aria-label="__('delete')"
                                                        :disabled="String(notificationsDeletingRuleId) === String(row.rule.id)"
                                                        @click="confirmDeleteRule(row.rule)" />
                                                </div>
                                            </td>
                                        </template>
                                    </tr>
                                    <tr v-if="tableRows.length === 0">
                                        <td colspan="5" class="empty-table">
                                            {{ __('notification_management.no_rules') }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </template>
            </div>
        </template>
    </side-panel>
</template>

<script>
    import { mapState } from 'vuex'
    import SidePanel from './SidePanel.vue'
    import NotificationRuleForm from './NotificationsPanel/NotificationRuleForm.vue'
    import { buildEventCategories } from './NotificationsPanel/eventCatalog'

    export default {
        name: 'NotificationsPanel',
        components: {
            NotificationRuleForm,
            SidePanel
        },
        data() {
            return {
                activeView: 'events',
                ruleFormOpen: false,
                selectedRule: null,
                ruleFormMode: 'create',
                filters: {
                    query: '',
                    searchBy: 'all',
                    event: 'all',
                    channel: 'all',
                    destination: 'all'
                }
            }
        },
        computed: {
            ...mapState({
                companyTags: (state) => state.geoFleet.companyTags,
                storeTrucks: (state) => state.geoFleet.trucks,
                storePois: (state) => state.pois.pois,
                poiCategories: (state) => state.pois.poiCategories,
                poisLoaded: (state) => state.pois.poisLoaded,
                notificationEvents: (state) => state.notifications.events,
                notificationsLoading: (state) => state.notifications.loading,
                notificationsError: (state) => state.notifications.error,
                notificationsSaving: (state) => state.notifications.saving,
                notificationsDeletingRuleId: (state) => state.notifications.deletingRuleId,
                notificationsDailyCap: (state) => state.notifications.dailyCap,
                notificationsCompanyId: (state) => state.notifications.companyId
            }),
            searchByOptions() {
                return [
                    { value: 'all', text: this.__('notification_management.search_everywhere') },
                    { value: 'plate', text: this.__('notification_management.search_by_plate') },
                    { value: 'tag', text: this.__('notification_management.search_by_tag') },
                    // {
                    //     value: 'event_name',
                    //     text: this.__('notification_management.search_by_event_name')
                    // }
                ]
            },
            eventCategories() {
                return buildEventCategories((key) => this.__(key))
                    .map((category) => ({
                        ...category,
                        events: category.events.filter((event) => !event.disabled)
                    }))
                    .filter((category) => category.events.length > 0)
            },
            eventLookup() {
                return this.eventCategories.reduce((lookup, category) => {
                    category.events.forEach((event) => {
                        lookup[event.value] = {
                            ...event,
                            category: category.key,
                            categoryLabel: category.label
                        }
                    })

                    return lookup
                }, {})
            },
            notificationRules() {
                return Object.entries(this.notificationEvents || {}).flatMap(
                    ([eventType, rules]) =>
                        (Array.isArray(rules) ? rules : []).map((rule) =>
                            this.normalizeApiRule(eventType, rule)
                        )
                )
            },
            filteredNotificationRules() {
                const query = this.normalizeSearchText(this.filters.query)
                const destination = this.parseDestinationFilter(this.filters.destination)

                return this.notificationRules.filter((rule) => {
                    if (this.filters.event !== 'all' && rule.event.type !== this.filters.event) {
                        return false
                    }
                    if (
                        this.filters.channel !== 'all' &&
                        !rule.channels[this.filters.channel]?.enabled
                    ) {
                        return false
                    }
                    if (
                        destination &&
                        !(rule.channels[destination.channel]?.recipients || []).some(
                            (recipient) => String(recipient) === destination.recipient
                        )
                    ) {
                        return false
                    }
                    if (!query) return true

                    const event = this.eventLookup[rule.event.type]
                    const scopes = Array.isArray(rule.scopes) ? rule.scopes : []
                    let searchableValues = []

                    if (this.filters.searchBy === 'plate') {
                        searchableValues = scopes.filter(
                            (scope) => scope !== '*' && !String(scope).startsWith('tag:')
                        )
                    } else if (this.filters.searchBy === 'tag') {
                        searchableValues = scopes
                            .filter((scope) => String(scope).startsWith('tag:'))
                            .map((scope) => String(scope).slice(4))
                    } else if (this.filters.searchBy === 'event_name') {
                        searchableValues = [rule.event.type, event?.label]
                    } else {
                        searchableValues = [
                            rule.ruleName,
                            rule.event.type,
                            event?.label,
                            event?.categoryLabel,
                            rule.application.label,
                            scopes.join(' '),
                            rule.mainParameters,
                            rule.language,
                            this.languageLabel(rule.language)
                        ]
                    }

                    return searchableValues.some((value) =>
                        this.normalizeSearchText(value).includes(query)
                    )
                })
            },
            hasActiveFilters() {
                return (
                    this.filters.query !== '' ||
                    this.filters.searchBy !== 'all' ||
                    this.filters.event !== 'all' ||
                    this.filters.channel !== 'all' ||
                    this.filters.destination !== 'all'
                )
            },
            tableRows() {
                if (this.activeView === 'events') {
                    const rows = []
                    const groupedRuleIds = new Set()

                    this.eventCategories.forEach((category) => {
                        category.events.forEach((event) => {
                            const eventRules = this.filteredNotificationRules.filter(
                                (rule) => rule.event.type === event.value
                            )
                            if (eventRules.length === 0) return

                            rows.push({
                                type: 'category',
                                key: `event-${event.value}`,
                                category: {
                                    key: event.value,
                                    icon: category.icon,
                                    label: event.label
                                },
                                ruleCount: eventRules.length
                            })

                            eventRules.forEach((rule) => {
                                groupedRuleIds.add(String(rule.id))
                                rows.push({
                                    type: 'rule',
                                    key: `event-${event.value}-rule-${rule.id}`,
                                    rule
                                })
                            })
                        })
                    })

                    const unknownEvents = this.filteredNotificationRules.reduce((events, rule) => {
                        if (groupedRuleIds.has(String(rule.id))) return events
                        const eventType = rule.event.type
                        if (!events[eventType]) events[eventType] = []
                        events[eventType].push(rule)
                        return events
                    }, {})

                    Object.entries(unknownEvents).forEach(([eventType, eventRules]) => {
                        rows.push({
                            type: 'category',
                            key: `event-${eventType}`,
                            category: {
                                key: eventType,
                                icon: 'fa-bell',
                                label: eventType
                            },
                            ruleCount: eventRules.length
                        })
                        eventRules.forEach((rule) => {
                            rows.push({
                                type: 'rule',
                                key: `event-${eventType}-rule-${rule.id}`,
                                rule
                            })
                        })
                    })

                    return rows
                }

                const rows = []
                const groupedRuleIds = new Set()

                this.eventCategories.forEach((category) => {
                    const categoryRules = this.filteredNotificationRules.filter((rule) => {
                        return this.eventLookup[rule.event.type]?.category === category.key
                    })

                    if (categoryRules.length === 0) return

                    rows.push({
                        type: 'category',
                        key: `category-${category.key}`,
                        category,
                        ruleCount: categoryRules.length
                    })

                    categoryRules.forEach((rule) => {
                        groupedRuleIds.add(rule.id)
                        rows.push({
                            type: 'rule',
                            key: `category-${category.key}-rule-${rule.id}`,
                            rule
                        })
                    })
                })

                // Keep future API event types visible even before they are added
                // to the local catalogue.
                this.filteredNotificationRules.forEach((rule) => {
                    if (groupedRuleIds.has(rule.id)) return

                    rows.push({
                        type: 'rule',
                        key: `ungrouped-rule-${rule.id}`,
                        rule
                    })
                })

                return rows
            },
            channelOptions() {
                return [
                    { value: 'all', text: this.__('notification_management.all_channels') },
                    { value: 'email', text: this.__('notification_management.email') },
                    { value: 'sms', text: this.__('notification_management.sms') }
                ]
            },
            destinationGroups() {
                return ['email', 'sms']
                    .map((channel) => {
                        const recipients = this.notificationRules.flatMap((rule) =>
                            (rule.channels[channel]?.recipients || []).map(String)
                        )
                        const destinations = [...new Set(recipients.filter(Boolean))]
                            .sort((a, b) => a.localeCompare(b))
                            .map((recipient) => ({
                                value: JSON.stringify([channel, recipient]),
                                label: recipient
                            }))

                        return {
                            channel,
                            label: this.__(`notification_management.${channel}`),
                            destinations
                        }
                    })
                    .filter((group) => group.destinations.length > 0)
            }
        },
        methods: {
            normalizeSearchText(value) {
                return String(value ?? '')
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .toLowerCase()
                    .trim()
            },
            languageLabel(locale) {
                if (!locale) return '-'

                const languageCode = String(locale)
                    .trim()
                    .replace('_', '-')
                    .split('-')[0]
                    .toLowerCase()
                const translatedLabels = {
                    it: this.__('notification_management.language_italian'),
                    en: this.__('notification_management.language_english')
                }

                if (translatedLabels[languageCode]) return translatedLabels[languageCode]

                try {
                    const displayNames = new Intl.DisplayNames([this.$locale() || 'it'], {
                        type: 'language'
                    })
                    const label = displayNames.of(languageCode)
                    return label
                        ? label.charAt(0).toLocaleUpperCase(this.$locale() || 'it') + label.slice(1)
                        : String(locale).toUpperCase()
                } catch (error) {
                    return String(locale).toUpperCase()
                }
            },
            parseDestinationFilter(value) {
                if (!value || value === 'all') return null

                try {
                    const [channel, recipient] = JSON.parse(value)
                    if (!['email', 'sms'].includes(channel) || !recipient) return null
                    return { channel, recipient: String(recipient) }
                } catch (error) {
                    return null
                }
            },
            resetFilters() {
                Object.assign(this.filters, {
                    query: '',
                    searchBy: 'all',
                    event: 'all',
                    channel: 'all',
                    destination: 'all'
                })
            },
            openCreateForm() {
                this.selectedRule = null
                this.ruleFormMode = 'create'
                this.ruleFormOpen = true
            },
            openEditForm(rule) {
                this.selectedRule = rule
                this.ruleFormMode = 'edit'
                this.ruleFormOpen = true
            },
            openCloneForm(rule) {
                const clonedRule = JSON.parse(JSON.stringify(rule))

                clonedRule.id = null
                clonedRule.ruleName = `${this.__('notification_management.copy_of')} ${rule.ruleName}`
                delete clonedRule.apiRule

                this.selectedRule = clonedRule
                this.ruleFormMode = 'clone'
                this.ruleFormOpen = true
            },
            closeRuleForm() {
                this.ruleFormOpen = false
                this.selectedRule = null
                this.ruleFormMode = 'create'
            },
            confirmDeleteRule(rule) {
                this.$snotify.confirm(
                    this.__('notification_management.delete_rule_confirmation').replace(
                        ':name',
                        rule.ruleName
                    ),
                    this.__('notification_management.delete_rule'),
                    {
                        position: 'centerTop',
                        timeout: 0,
                        buttons: [
                            {
                                text: this.__('yes'),
                                action: (toast) => {
                                    this.$snotify.remove(toast.id)
                                    this.deleteRule(rule)
                                },
                                bold: true
                            },
                            {
                                text: this.__('no'),
                                action: (toast) => this.$snotify.remove(toast.id)
                            }
                        ]
                    }
                )
            },
            async deleteRule(rule) {
                try {
                    await this.$store.dispatch('notifications/deleteNotification', {
                        vueInstance: this,
                        ruleId: rule.id
                    })
                    this.$snotify.success(
                        this.__('notification_management.delete_rule_success'),
                        { position: 'centerTop' }
                    )
                    await this.loadNotificationRules()
                } catch (error) {
                    this.$snotify.error(
                        this.__('notification_management.delete_rule_error'),
                        { position: 'centerTop' }
                    )
                }
            },
            async saveRule(form) {
                const isUpdate = form.id != null
                const action = isUpdate
                    ? 'notifications/updateNotification'
                    : 'notifications/createNotification'
                const request = {
                    vueInstance: this,
                    payload: this.buildCreatePayload(form)
                }
                if (isUpdate) request.ruleId = form.id

                try {
                    await this.$store.dispatch(action, request)
                    this.$snotify.success(
                        this.__(
                            isUpdate
                                ? 'notification_management.update_rule_success'
                                : 'notification_management.create_rule_success'
                        ),
                        { position: 'centerTop' }
                    )
                    this.closeRuleForm()
                    await this.loadNotificationRules()
                } catch (error) {
                    this.$snotify.error(
                        this.__(
                            isUpdate
                                ? 'notification_management.update_rule_error'
                                : 'notification_management.create_rule_error'
                        ),
                        { position: 'centerTop' }
                    )
                }
            },
            buildCreatePayload(form) {
                const scopes =
                    form.target.scope === 'fleet'
                        ? ['*']
                        : [
                            ...form.target.tagIds
                                .map(
                                    (tagId) =>
                                        this.companyTags.find((tag) => tag.id === tagId)?.name
                                )
                                .filter(Boolean)
                                .map((tagName) => `tag:${tagName}`),
                            ...form.target.vehicleIds
                                .map(
                                    (vehicleId) =>
                                        this.storeTrucks.find((truck) => truck.id === vehicleId)
                                            ?.plate
                                )
                                .filter(Boolean)
                        ]
                const channels = ['email', 'sms']
                    .filter((type) => form.channels[type].enabled)
                    .map((type) => ({
                        type,
                        [type === 'email' ? 'addresses' : 'numbers']: [
                            ...form.channels[type].recipients
                        ]
                    }))
                const parameters = Object.entries(form.parameters).reduce(
                    (payload, [key, value]) => {
                        if (value !== null && value !== '') payload[key] = value
                        return payload
                    },
                    {}
                )

                return {
                    event: form.eventType,
                    name: form.ruleName,
                    scopes,
                    channels,
                    language: form.language,
                    ...parameters
                }
            },
            normalizeApiRule(eventType, rule) {
                const scopes = Array.isArray(rule.scopes) ? rule.scopes : []
                const isFleet = scopes.includes('*')
                const tagNames = [
                    ...new Set(
                        scopes
                            .filter((scope) => String(scope).startsWith('tag:'))
                            .map((scope) => String(scope).slice(4))
                            .filter(Boolean)
                    )
                ]
                const plates = [
                    ...new Set(
                        scopes
                            .filter(
                                (scope) => !String(scope).startsWith('tag:') && scope !== '*'
                            )
                            .map(String)
                            .filter(Boolean)
                    )
                ]
                const tagIds = tagNames
                    .map(
                        (tagName) =>
                            this.companyTags.find((tag) => tag.name === tagName)?.id
                    )
                    .filter((tagId) => tagId != null)
                const vehicleIds = plates
                    .map(
                        (plate) =>
                            this.storeTrucks.find((truck) => truck.plate === plate)?.id
                    )
                    .filter((vehicleId) => vehicleId != null)
                const email = this.normalizeApiChannel(rule, 'email')
                const sms = this.normalizeApiChannel(rule, 'sms')
                const parameters = this.extractApiParameters(eventType, rule)

                return {
                    id: rule.id,
                    ruleName: rule.name || '',
                    event: { type: eventType },
                    target: {
                        scope: isFleet ? 'fleet' : 'specific',
                        tagIds,
                        vehicleIds
                    },
                    application: {
                        type: isFleet ? 'fleet' : 'selection',
                        label: isFleet
                            ? this.__('notification_management.whole_fleet')
                            : scopes.join(', ') || '-',
                        tags: tagNames,
                        plates
                    },
                    parameters,
                    mainParameters:
                        this.buildParametersSummary(eventType, parameters) || '-',
                    channels: { email, sms },
                    recipientsTotal: email.count + sms.count,
                    language: rule.language || null,
                    scopes,
                    apiRule: rule
                }
            },
            normalizeApiChannel(rule, type) {
                const channels = rule.channels
                let definition = null

                if (Array.isArray(channels)) {
                    definition = channels.find((channel) => {
                        if (typeof channel === 'string') return channel === type
                        return [channel?.type, channel?.channel, channel?.name].includes(type)
                    })
                } else if (channels && typeof channels === 'object') {
                    definition = channels[type] || null
                }

                const recipients = Array.isArray(definition?.addresses)
                    ? definition.addresses
                    : Array.isArray(definition?.numbers)
                        ? definition.numbers
                        : Array.isArray(definition?.recipients)
                            ? definition.recipients
                            : []
                const count = Number(rule.recipients_count?.[type]) || recipients.length

                return {
                    enabled: definition != null || count > 0,
                    count,
                    recipients
                }
            },
            extractApiParameters(eventType, rule) {
                const parameterKeys = {
                    poi_in: ['poi_ids', 'poi_categories', 'cooldown_minutes'],
                    poi_out: ['poi_ids', 'poi_categories', 'cooldown_minutes'],
                    vehicle_stationary: [
                        'duration_hours',
                        'exclude_pois',
                        'exclude_poi_categories',
                        'cooldown_hours',
                        'notify_for_hours'
                    ],
                    low_temperature: ['cooldown_minutes'],
                    high_temperature: ['cooldown_minutes'],
                    door_open: ['cooldown_minutes'],
                    door_closed: ['cooldown_minutes']
                }

                return (parameterKeys[eventType] || []).reduce((parameters, key) => {
                    if (Object.prototype.hasOwnProperty.call(rule, key)) {
                        parameters[key] = rule[key]
                    }
                    return parameters
                }, {})
            },
            loadNotificationRules() {
                return this.$store
                    .dispatch('notifications/loadNotifications', {
                        vueInstance: this,
                        force: true
                    })
                    .catch(() => null)
            },
            buildParametersSummary(eventType, parameters) {
                const parts = []
                const poiNames = (poiIds) =>
                    (poiIds || [])
                        .map((poiId) => this.storePois.find((poi) => poi.id === poiId)?.description)
                        .filter(Boolean)
                        .join(', ')
                const poiCategoryNames = (categoryIds) =>
                    (categoryIds || [])
                        .map(
                            (categoryId) =>
                                this.poiCategories.find((category) => category.id === categoryId)
                                    ?.description
                        )
                        .filter(Boolean)
                        .join(', ')

                if (['poi_in', 'poi_out'].includes(eventType)) {
                    const monitoredPois = poiNames(parameters.poi_ids)
                    if (monitoredPois) {
                        parts.push(
                            `${this.__('notification_management.pois_to_monitor')}: ${monitoredPois}`
                        )
                    }
                    const monitoredCategories = poiCategoryNames(parameters.poi_categories)
                    if (monitoredCategories) {
                        parts.push(
                            `${this.__('notification_management.poi_categories_to_monitor')}: ${monitoredCategories}`
                        )
                    }
                }
                if (eventType === 'vehicle_stationary') {
                    if (parameters.duration_hours != null) {
                        parts.push(
                            `${this.__('notification_management.stopped_after_hours')}: ${parameters.duration_hours} ${this.__('notification_management.hours_unit')}`
                        )
                    }
                    const excludedPois = poiNames(parameters.exclude_pois)
                    if (excludedPois) {
                        parts.push(
                            `${this.__('notification_management.excluded_stop_pois')}: ${excludedPois}`
                        )
                    }
                    const excludedCategories = poiCategoryNames(parameters.exclude_poi_categories)
                    if (excludedCategories) {
                        parts.push(
                            `${this.__('notification_management.excluded_stop_poi_categories')}: ${excludedCategories}`
                        )
                    }
                    if (parameters.cooldown_hours != null) {
                        parts.push(
                            `${this.__('notification_management.minimum_notification_interval')}: ${parameters.cooldown_hours} ${this.__('notification_management.hours_unit')}`
                        )
                    }
                    if (parameters.notify_for_hours != null) {
                        parts.push(
                            `${this.__('notification_management.notify_for_hours')}: ${parameters.notify_for_hours} ${this.__('notification_management.hours_unit')}`
                        )
                    }
                } else if (parameters.cooldown_minutes != null) {
                    parts.push(
                        `${this.__('notification_management.minimum_notification_interval')}: ${parameters.cooldown_minutes} ${this.__('notification_management.minutes_unit')}`
                    )
                }

                return parts.join(' · ')
            },
            channelTitle(type, channel) {
                const label = this.__(`notification_management.${type}`)
                const state = this.__(
                    channel.enabled
                        ? 'notification_management.channel_enabled'
                        : 'notification_management.channel_disabled'
                )

                return `${label}: ${state}`
            },
            recipientsSummary(rule) {
                const total = Number(rule.recipientsTotal) || 0
                const key =
                    total === 1
                        ? 'notification_management.recipient_total'
                        : 'notification_management.recipients_total'

                return this.__(key).replace(':count', total)
            }
        },
        mounted() {
            this.loadNotificationRules()
            if (!this.poisLoaded) {
                this.$store.dispatch('loadPois', { vueInstance: this })
            }
            if (this.poiCategories.length === 0) {
                this.$store.dispatch('loadPoiCategories', { vueInstance: this })
            }
        }
    }
</script>

<style scoped lang="scss">
    @use './SidePanel.scss';

    #notifications-panel {
        flex: 1;
        width: 100%;
        min-width: 0;
        padding: 15px;

        ::v-deep(.head),
        ::v-deep(.body) {
            width: 100%;
        }
    }

    .notifications-content {
        display: flex;
        flex: 1;
        flex-direction: column;
        gap: 18px;
        min-width: 0;
        padding: 0 15px 15px;
    }

    .notifications-filters {
        display: flex;
        width: 100%;
        align-items: center;
        gap: 12px;
        padding: 14px;
        border: 1px solid var(--main-gray);
        border-radius: 14px;
        background: var(--main-inner-bg-color);
    }

    .search-filter {
        flex: 2 1 520px;
        min-width: 380px;
        flex-wrap: nowrap;

        .search-input {
            min-width: 120px;
        }

        .search-scope {
            flex: 0 1 150px;
            width: 150px;
            max-width: 150px;
        }
    }

    .notification-control,
    .input-group-text {
        min-height: 42px;
        border: 1px solid #d2d2d2;
        background-color: #fff;
        color: #333;
        font-size: 14px;
    }

    .notification-control {
        padding: 8px 12px;
        border-radius: 12px;

        &:focus {
            border-color: #d2d2d2;
            background-color: var(--main-gray);
            color: #333;
            box-shadow: none;
        }
    }

    .input-group-text {
        min-width: 42px;
        justify-content: center;
        border-radius: 12px 0 0 12px;
    }

    .input-group>.search-input {
        border-radius: 0;
    }

    .input-group>.search-scope {
        padding-right: 32px;
        border-radius: 0 12px 12px 0;
    }

    .filter-select {
        flex: 1 1 200px;
        min-width: 170px;
        max-width: 260px;
    }

    .channel-filter {
        flex-basis: 150px;
        min-width: 140px;
        max-width: 180px;
    }

    .new-rule-button,
    .reset-filters-button {
        flex: 0 0 auto;
        white-space: nowrap;
    }

    .notification-views {
        display: flex;
        flex: 1;
        flex-direction: column;
        gap: 12px;
        min-width: 0;
    }

    .view-tabs {
        display: inline-flex;
        align-self: flex-start;
        gap: 4px;
        padding: 4px;
        border-radius: 12px;
        background: var(--main-inner-bg-color);
    }

    .view-tab {
        border: 0;
        border-radius: 9px;
        padding: 9px 16px;
        background: transparent;
        color: var(--main-text-color);
        font-family: 'Stolzl-Regular';
        font-size: 13px;
        cursor: pointer;
        transition:
            background-color 0.18s ease,
            color 0.18s ease;

        &:hover:not(.active) {
            background: var(--main-gray);
        }

        &:focus-visible {
            outline: 2px solid var(--dark-blue);
            outline-offset: 2px;
        }

        &.active {
            background: var(--dark-blue);
            color: #fff;
        }
    }

    .notification-table-wrapper {
        flex: 1;
        min-width: 0;
        border-radius: 14px;
        background: var(--main-inner-bg-color);
        overflow: auto;
        padding: 0;
        border: 1px solid #d2d2d2;
    }

    .notifications-state {
        display: flex;
        min-height: 180px;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding: 24px;
        color: #6b7785;
        font-size: 13px;

        >i {
            color: var(--dark-blue);
            font-size: 18px;
        }

        &.error-state {
            flex-direction: column;
            text-align: center;

            >i {
                color: var(--danger);
            }
        }
    }

    .notification-table {
        width: 100%;
        min-width: 850px;
        border-collapse: collapse;
        color: var(--main-text-color);
        font-family: 'Stolzl-Regular';

        th {
            padding: 16px 8px;
            color: #6b7785;
            font-size: 13px;
            font-weight: 600;
            text-align: left;
            text-transform: uppercase;
            line-height: 1;

            &:first-child {
                padding-left: 16px;
            }

            &:last-child {
                padding-right: 16px;
            }
        }

        td {
            padding: 12px 8px;
            border-top: 1px solid #d2d2d2;
            font-size: 13px;
            vertical-align: middle;

            &:first-child {
                padding-left: 16px;
            }

            &:last-child {
                padding-right: 16px;
            }
        }
    }

    .rule-actions {
        display: flex;
        justify-content: center;
        gap: 8px;

        .btn {
            border-radius: 60px;
            font-size: 12px;
            border: none;
        }
    }

    .rule-identity {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 5px;

        strong {
            font-family: 'Stolzl-Medium';
            font-size: 14px;
            font-weight: 500;
        }
    }

    .event-name,
    .parameters-description,
    .recipients-summary {
        color: #6b7785;
    }

    .event-name {
        font-size: 12px;
    }

    .rule-chip {
        display: inline-flex;
        align-items: center;
        width: fit-content;
        border-radius: 999px;
        padding: 4px 9px;
        font-size: 11px;
        line-height: 1.2;
    }

    .event-category {
        background: rgba(29, 95, 159, 0.12);
        color: #1d5f9f;
    }

    .application-chip {
        background: var(--main-gray);
        color: var(--main-text-color);
    }

    .application-groups {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 5px;
        font-size: 11px;
    }

    .application-group {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        line-height: 1.3;

        strong {
            font-family: 'Stolzl-Medium';
            font-weight: 500;
        }
    }

    .parameters-description {
        max-width: 360px;
        margin: 0;
        line-height: 1.2;
        font-size: 10px;
        font-family: 'Stolzl-Light';
        color: var(--main-text-color);
    }

    .rule-channels {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
    }

    .channel-list {
        display: flex;
        gap: 10px;
        width: 100%;
    }

    .channel-status {
        position: relative;
        display: inline-flex;
        width: 34px;
        height: 34px;
        align-items: center;
        justify-content: center;
        border: 1px solid #d2d2d2;
        border-radius: 60px;
        background: var(--main-gray);
        color: #9aa4af;
        font-size: 15px;

        &.enabled {
            border-color: #1d5f9f;
            background: white;
            color: #1d5f9f;
        }
    }

    .channel-counter {
        position: absolute;
        top: -7px;
        right: -7px;
        display: flex;
        min-width: 18px;
        height: 18px;
        align-items: center;
        justify-content: center;
        border: 2px solid var(--main-inner-bg-color);
        border-radius: 999px;
        padding: 0 4px;
        background: var(--dark-blue);
        color: #fff;
        font-size: 9px;
        line-height: 1;
    }

    .recipients-summary {
        font-size: 10px;
        font-family: 'Stolzl-Light';
        color: var(--main-text-color);
    }

    .empty-table {
        padding: 38px 12px !important;
        color: #6b7785;
        text-align: center;
    }

    .category-group-row .category-group-cell {
        color: var(--main-text-color);
        background: #dcdfe7;

        strong {
            margin-left: 10px;
            font-family: 'Stolzl-Medium';
            font-size: 14px;
            vertical-align: middle;
        }
    }

    .category-icon {
        display: inline-flex;
        width: 34px;
        height: 34px;
        flex: 0 0 34px;
        align-items: center;
        justify-content: center;
        border-radius: 10px;
        background: rgba(29, 95, 159, 0.12);
        color: #1d5f9f;
    }

    .category-rule-counter {
        display: inline-flex;
        min-width: 22px;
        height: 22px;
        align-items: center;
        justify-content: center;
        margin-left: 8px;
        border-radius: 999px;
        padding: 0 7px;
        background: var(--dark-blue);
        color: #fff;
        font-family: 'Stolzl-Medium';
        font-size: 10px;
        line-height: 1;
        vertical-align: middle;
    }

    @media (max-width: 1200px) {
        .notifications-filters {
            flex-wrap: wrap;
        }

        .search-filter {
            flex-basis: 100%;
            min-width: 100%;
        }
    }

    /* Keep horizontal scrolling local to the table. The panel body and filter
       toolbar must always fit the available space beside the vehicle list. */
    #notifications-panel ::v-deep(.body) {
        min-width: 0;
        overflow-x: hidden;
    }

    .notifications-content,
    .notifications-filters,
    .notification-views,
    .notification-table-wrapper {
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
    }

    .notifications-content {
        overflow-x: hidden;
    }

    .notifications-filters>* {
        max-width: 100%;
    }

    @media (max-width: 1640px) {
        .notifications-filters {
            flex-wrap: wrap;
        }

        .search-filter {
            width: 100%;
            min-width: 0;
            flex: 1 1 100%;
        }

        .filter-select,
        .channel-filter {
            min-width: 0;
            max-width: none;
            flex: 1 1 180px;
        }
    }

    @media (max-width: 800px) {

        .filter-select,
        .channel-filter {
            width: calc(50% - 6px);
            flex: 1 1 calc(50% - 6px);
        }

        .new-rule-button,
        .reset-filters-button {
            width: 100%;
            flex-basis: 100%;
        }

        .new-rule-button ::v-deep(button),
        .reset-filters-button ::v-deep(button) {
            width: 100%;
        }
    }

    @media (max-width: 560px) {
        .search-filter .search-scope {
            width: 150px;
            max-width: 150px;
            flex-basis: 150px;
        }

        .search-filter .search-input {
            min-width: 0;
        }

        .filter-select,
        .channel-filter {
            width: 100%;
            flex-basis: 100%;
        }

        .view-tabs {
            width: 100%;
            flex-direction: column;
        }

        .view-tab {
            width: 100%;
        }
    }
</style>
